import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, ACADEMY } from "../../lib/constants";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  // On non-home pages we always show the solid glass style
  const solid = scrolled || !isHome || open;

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-[60] transition-colors duration-500 ${
          solid ? "glass-dark" : "bg-transparent"
        }`}
        data-testid="main-navbar"
      >
        <nav className="rb-container flex items-center justify-between py-4 md:py-5">
          <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
            <img src="/rythmbara-logo.png" alt="Rythmbara Dance Academy" className="h-14 w-14 sm:h-16 sm:w-16 rounded-full drop-shadow-[0_2px_10px_rgba(212,175,55,0.35)] transition-transform duration-500 group-hover:rotate-6" />
            <span className="flex flex-col leading-none">
              <span className="font-serif text-2xl md:text-[28px] font-medium text-[#F6F4EE] tracking-tight">
                Rythm<span className="text-gold">bara</span>
              </span>
              <span className="overline text-[9px] text-[#F6F4EE]/60 mt-1">Dance Academy</span>
            </span>
          </Link>

          <ul className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `relative text-sm tracking-wide transition-colors duration-300 ${
                      isActive ? "text-gold" : "text-[#F6F4EE]/85 hover:text-gold"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3">
            <a
              href={`tel:${ACADEMY.phoneRaw}`}
              data-testid="nav-call-btn"
              className="btn-gold hidden sm:inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium tracking-wide"
            >
              <Phone className="h-4 w-4" /> Call Now
            </a>
            <button
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden text-[#F6F4EE] p-1"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[55] lg:hidden glass-dark pt-24"
            data-testid="mobile-menu"
          >
            <ul className="flex flex-col rb-container gap-2">
              {NAV_LINKS.map((l, i) => (
                <motion.li
                  key={l.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    className="block py-4 font-serif text-3xl text-[#F6F4EE] border-b border-[rgba(212,175,55,0.15)]"
                  >
                    {l.label}
                  </NavLink>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
